<template>
  <div class="failed-notifications">
    <div class="notifications-header">
      <div class="header-left">
        <span class="header-title">
          <i class="el-icon-warning"></i>
          Failed Alerts
          <el-badge v-if="unreadCount > 0" :value="unreadCount" class="unread-badge" />
        </span>
        <span v-if="activeMessages.length > 0" class="total-count">
          ({{ activeMessages.length }} total)
        </span>
      </div>
      <div class="header-actions" v-if="activeMessages.length > 0">
        <el-button type="text" size="mini" @click="markAllAsRead">
          Mark All Read
        </el-button>
        <el-button type="text" size="mini" class="clear-btn" @click="clearAll">
          Clear All
        </el-button>
      </div>
    </div>

    <div class="notifications-list">
      <div v-if="activeMessages.length === 0" class="no-notifications">
        <i class="el-icon-circle-check"></i>
        <span>No failed messages</span>
      </div>

      <div
        v-for="message in visibleMessages"
        :key="message._id"
        :class="['notification-item', { unread: isUnread(message._id) }]"
      >
        <div class="notification-content">
          <div class="notification-phone">
            <span class="status-dot" :class="{ unread: isUnread(message._id) }"></span>
            {{ message.receiver_ws_number }}
          </div>
          <div class="notification-template" v-if="message.template_id">
            {{ message.template_id }}
          </div>
          <div class="notification-error">
            {{ message.error_log || 'Unknown error' }}
          </div>
          <div class="notification-footer">
            <span class="notification-time">{{ formatTimeAgo(message.timestamp) }}</span>
            <div class="notification-actions">
              <!-- <el-tooltip content="View in table" placement="top">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-view"
                  @click="viewDetails(message)"
                />
              </el-tooltip> -->
              <el-tooltip v-if="isUnread(message._id)" content="Mark as read" placement="top">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-check"
                  @click="markAsRead(message._id)"
                />
              </el-tooltip>
              <el-tooltip content="Dismiss" placement="top">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-close"
                  @click="dismissNotification(message._id)"
                />
              </el-tooltip>
            </div>
          </div>
        </div>
      </div>

      <!-- Load More Button -->
      <div v-if="hasMoreMessages" class="load-more">
        <el-button type="text" size="small" @click="loadMore">
          <i class="el-icon-arrow-down"></i>
          Load More ({{ remainingCount }} remaining)
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";

const STORAGE_KEY_PREFIX = "whatsapp_failed_notifications_";
const ITEMS_PER_PAGE = 20;
const MAX_STORED_IDS = 500; // Limit localStorage entries
const CLEANUP_DAYS = 7; // Remove read IDs older than 7 days

export default {
  name: "FailedMessageNotifications",
  props: {
    waEndpoint: {
      type: String,
      required: true,
    },
    waId: {
      type: String,
      required: true,
    },
    failedMessages: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      readMessageIds: [], // Array of { id, timestamp }
      dismissedIds: [],
      displayLimit: ITEMS_PER_PAGE,
    };
  },
  computed: {
    storageKey() {
      return `${STORAGE_KEY_PREFIX}${this.waId}`;
    },
    // Filter out dismissed messages
    activeMessages() {
      return this.failedMessages.filter((m) => !this.dismissedIds.includes(m._id));
    },
    unreadCount() {
      const readIds = this.readMessageIds.map((r) => r.id);
      return this.activeMessages.filter((m) => !readIds.includes(m._id)).length;
    },
    sortedMessages() {
      const readIds = this.readMessageIds.map((r) => r.id);
      // Sort by unread first, then by timestamp descending
      return _.orderBy(
        this.activeMessages,
        [
          (m) => (readIds.includes(m._id) ? 1 : 0),
          (m) => new Date(m.timestamp).getTime(),
        ],
        ["asc", "desc"]
      );
    },
    visibleMessages() {
      return this.sortedMessages.slice(0, this.displayLimit);
    },
    hasMoreMessages() {
      return this.sortedMessages.length > this.displayLimit;
    },
    remainingCount() {
      return this.sortedMessages.length - this.displayLimit;
    },
  },
  methods: {
    loadFromStorage() {
      try {
        const stored = localStorage.getItem(this.storageKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          this.readMessageIds = parsed.readIds || [];
          this.dismissedIds = parsed.dismissedIds || [];
        }
      } catch (e) {
        console.error("Error loading from storage:", e);
        this.readMessageIds = [];
        this.dismissedIds = [];
      }
    },
    saveToStorage() {
      try {
        const data = {
          readIds: this.readMessageIds.slice(-MAX_STORED_IDS), // Keep only recent
          dismissedIds: this.dismissedIds.slice(-MAX_STORED_IDS),
        };
        localStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (e) {
        console.error("Error saving to storage:", e);
      }
    },
    isUnread(messageId) {
      return !this.readMessageIds.some((r) => r.id === messageId);
    },
    markAsRead(messageId) {
      if (!this.readMessageIds.some((r) => r.id === messageId)) {
        this.readMessageIds.push({ id: messageId, timestamp: Date.now() });
        this.saveToStorage();
      }
    },
    markAllAsRead() {
      const now = Date.now();
      const newReadIds = this.activeMessages
        .filter((m) => !this.readMessageIds.some((r) => r.id === m._id))
        .map((m) => ({ id: m._id, timestamp: now }));
      this.readMessageIds = [...this.readMessageIds, ...newReadIds];
      this.saveToStorage();
    },
    dismissNotification(messageId) {
      if (!this.dismissedIds.includes(messageId)) {
        this.dismissedIds.push(messageId);
        this.saveToStorage();
      }
    },
    clearAll() {
      this.$confirm("Clear all failed message notifications?", "Confirm", {
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        type: "warning",
      })
        .then(() => {
          const allIds = this.failedMessages.map((m) => m._id);
          this.dismissedIds = [...new Set([...this.dismissedIds, ...allIds])];
          this.saveToStorage();
          this.$message({ type: "success", message: "All notifications cleared" });
        })
        .catch(() => {});
    },
    loadMore() {
      this.displayLimit += ITEMS_PER_PAGE;
    },
    viewDetails(message) {
      this.markAsRead(message._id);
      this.$emit("view-details", message);
    },
    formatTimeAgo(timestamp) {
      return moment(timestamp).fromNow();
    },
    cleanupOldEntries() {
      const cutoffTime = Date.now() - CLEANUP_DAYS * 24 * 60 * 60 * 1000;

      // Remove read IDs older than CLEANUP_DAYS
      const beforeReadCount = this.readMessageIds.length;
      this.readMessageIds = this.readMessageIds.filter((r) => r.timestamp > cutoffTime);

      // Only clean up dismissed IDs if we have messages to compare against
      // This prevents clearing dismissedIds when failedMessages prop is still empty
      let dismissedChanged = false;
      if (this.failedMessages.length > 0) {
        const currentIds = this.failedMessages.map((m) => m._id);
        const beforeDismissedCount = this.dismissedIds.length;
        this.dismissedIds = this.dismissedIds.filter((id) => currentIds.includes(id));
        dismissedChanged = beforeDismissedCount !== this.dismissedIds.length;
      }

      if (beforeReadCount !== this.readMessageIds.length || dismissedChanged) {
        this.saveToStorage();
      }
    },
    resetDisplayLimit() {
      this.displayLimit = ITEMS_PER_PAGE;
    },
  },
  mounted() {
    this.loadFromStorage();
  },
  watch: {
    waEndpoint() {
      this.loadFromStorage();
      this.resetDisplayLimit();
    },
    waId() {
      this.loadFromStorage();
      this.resetDisplayLimit();
    },
    failedMessages() {
      this.cleanupOldEntries();
    },
    unreadCount: {
      immediate: true,
      handler(newCount) {
        this.$emit("unread-count-change", newCount);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.failed-notifications {
  .notifications-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 10px;
    border-bottom: 1px solid #ebeef5;
    margin-bottom: 10px;

    .header-left {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .header-title {
        font-weight: 600;
        font-size: 14px;
        color: #303133;
        display: flex;
        align-items: center;
        gap: 6px;

        .el-icon-warning {
          color: #e6a23c;
        }

        .unread-badge {
          margin-left: 4px;
        }
      }

      .total-count {
        font-size: 11px;
        color: #909399;
      }
    }

    .header-actions {
      display: flex;
      gap: 4px;

      .clear-btn {
        color: #f56c6c;
      }
    }
  }

  .notifications-list {
    max-height: 280px;
    overflow-y: auto;

    .no-notifications {
      text-align: center;
      padding: 20px;
      color: #909399;

      i {
        font-size: 24px;
        display: block;
        margin-bottom: 8px;
        color: #67c23a;
      }
    }

    .notification-item {
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 8px;
      border-left: 3px solid #dcdfe6;
      background-color: #fafafa;
      transition: all 0.3s ease;

      &.unread {
        border-left-color: #f56c6c;
        background-color: #fef0f0;
      }

      &:hover {
        background-color: #f5f7fa;
      }

      .notification-content {
        .notification-phone {
          font-weight: 600;
          font-size: 13px;
          color: #303133;
          display: flex;
          align-items: center;
          gap: 6px;

          .status-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background-color: #dcdfe6;

            &.unread {
              background-color: #f56c6c;
            }
          }
        }

        .notification-template {
          font-size: 12px;
          color: #606266;
          margin-top: 4px;
          padding-left: 14px;
        }

        .notification-error {
          font-size: 12px;
          color: #f56c6c;
          margin-top: 4px;
          padding-left: 14px;
          word-break: break-word;
        }

        .notification-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 6px;
          padding-left: 14px;

          .notification-time {
            font-size: 11px;
            color: #909399;
          }

          .notification-actions {
            display: flex;
            gap: 2px;

            .el-button {
              padding: 2px 4px;
            }
          }
        }
      }
    }

    .load-more {
      text-align: center;
      padding: 8px 0;
      border-top: 1px dashed #ebeef5;
      margin-top: 4px;

      .el-button {
        color: #409eff;

        i {
          margin-right: 4px;
        }
      }
    }
  }
}
</style>
